/**
 * Recurrence Series Service
 *
 * Client for the recurrence-series API. Wraps the CRUD + per-occurrence
 * mutation endpoints exposed by RecurrenceSeriesController.
 *
 * Backend endpoints:
 *   GET    /api/v1/recurrence-series?type=work_order|inspection
 *   GET    /api/v1/recurrence-series/:id
 *   POST   /api/v1/recurrence-series
 *   PATCH  /api/v1/recurrence-series/:id
 *   DELETE /api/v1/recurrence-series/:id
 *   PATCH  /api/v1/recurrence-series/:id/occurrences/:occId
 *   POST   /api/v1/recurrence-series/:id/occurrences/:occId/cancel
 *   DELETE /api/v1/recurrence-series/:id/occurrences/:occId
 *
 * All endpoints require authentication and admin/super_admin scope for
 * mutation. The standard CompanyScoped trait limits visibility to the
 * caller's accessible companies.
 */
import { api } from './api';
import type { RecurrenceRule } from '../utils/recurrence';

/* ============================================
 * TYPES
 * ============================================ */

export type SeriesType = 'work_order' | 'inspection';

/**
 * The minimal shape returned from the list endpoint. Detail views get
 * the same shape plus eager-loaded `workOrders` or `inspections`.
 */
export interface RecurrenceSeriesRecord {
	id: number;
	type: SeriesType;
	company_id: string | number;
	location_id: number | null;
	name: string;
	frequency_unit: 'day' | 'week' | 'month' | 'year';
	interval: number;
	by_weekday: number[];
	start_date: string;
	end_type: 'never' | 'on' | 'after';
	end_date: string | null;
	end_count: number | null;
	/** The next date a job is owed — created on that day by the server's
	 *  scheduler. Null once the series has ended or been cancelled. */
	next_occurrence_date: string | null;
	status: 'active' | 'paused' | 'cancelled' | 'ended';
	template_payload: Record<string, unknown>;
	created_by: number | null;
	created_at: string;
	updated_at: string;
	/* Eager-loaded relations (detail view only). */
	location?: { id: number; name: string } | null;
	company?: { company_id: number | string; name: string } | null;
	creator?: { id: number; first_name: string; last_name: string } | null;
	work_orders?: SeriesOccurrence[];
	inspections?: SeriesOccurrence[];
}

/** Generic occurrence shape — fields shared by WOs and Inspections. */
export interface SeriesOccurrence {
	id: number;
	recurrence_series_id: number;
	occurrence_state: 'scheduled' | 'cancelled' | 'deleted';
	cancelled_at: string | null;
	cancelled_reason: string | null;
	status: string;
	/* Date column differs by type — both are surfaced so callers can
	   pluck whichever applies. */
	scheduled_date?: string | null;
	inspection_date?: string | null;
	project_name?: string | null;
	assignee?: { id: number; first_name: string; last_name: string } | null;
	cancelled_by?: { id: number; first_name: string; last_name: string } | null;
}

export interface CreateSeriesPayload {
	type: SeriesType;
	rule: RecurrenceRule;
	/** Template payload — snake_case fields matching the WO/Inspection fillables. */
	template: Record<string, unknown>;
}

export interface CreateSeriesResponse {
	series: RecurrenceSeriesRecord;
	first_occurrence_id: number | null;
	occurrence_count: number;
}

export interface UpdateSeriesPayload {
	rule?: RecurrenceRule;
	template?: Record<string, unknown>;
	/** Edit scope — 'future' (default) | 'all'. 'this' is a per-occurrence
	 *  edit and goes through reschedule() instead. */
	scope?: 'this' | 'future' | 'all';
	/** YYYY-MM-DD reference date for scope='future'. Defaults to today. */
	reference_date?: string;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const recurrenceSeriesService = {
	list: (type?: SeriesType) =>
		api.get<{ data: RecurrenceSeriesRecord[] }>('/recurrence-series', type ? { type } : undefined),

	getById: (id: number | string) =>
		api.get<{ data: RecurrenceSeriesRecord }>(`/recurrence-series/${id}`),

	create: (payload: CreateSeriesPayload) =>
		api.post<{ data: CreateSeriesResponse }>('/recurrence-series', payload as unknown as Record<string, unknown>),

	update: (id: number | string, payload: UpdateSeriesPayload) =>
		api.patch<{ data: RecurrenceSeriesRecord }>(`/recurrence-series/${id}`, payload as unknown as Record<string, unknown>),

	delete: (id: number | string) =>
		api.delete<void>(`/recurrence-series/${id}`),

	rescheduleOccurrence: (seriesId: number | string, occurrenceId: number | string, date: string) =>
		api.patch<{ data: SeriesOccurrence }>(
			`/recurrence-series/${seriesId}/occurrences/${occurrenceId}`,
			{ date },
		),

	cancelOccurrence: (seriesId: number | string, occurrenceId: number | string, reason?: string) =>
		api.post<{ data: SeriesOccurrence }>(
			`/recurrence-series/${seriesId}/occurrences/${occurrenceId}/cancel`,
			reason ? { reason } : {},
		),

	deleteOccurrence: (seriesId: number | string, occurrenceId: number | string) =>
		api.delete<{ data: SeriesOccurrence }>(
			`/recurrence-series/${seriesId}/occurrences/${occurrenceId}`,
		),
};
